import React from 'react';
import { IoClose } from 'react-icons/io5';
import { useParams } from 'react-router-dom';
import { ActionIcon, Button, Checkbox, Modal, Select, Text } from 'rizzui';
import { toast } from 'sonner';
import useHandleErrors from '../../../../../hooks/useHandleErrors';
import { GroupCodeService } from '../../../../../services/groupCode/groupCodeService';

interface ContractForGroupCode {
    id: string;
    code: string;
    organisingCorporationId: string | null;
    organisingCorporationName: string;
    clientName: string;
    clientNif: string;
    studentName: string;
    studentNif: string;
}

interface AssignGroupCodeModalProps {
    isOpen: boolean;
    onClose: () => void;
    contracts: ContractForGroupCode[];
    refetch: () => void;
}

interface ExistingGroupCode {
    id: string;
    code: string;
    corporationId?: string | null;
    startDate?: string | { date?: string } | null;
    endDate?: string | { date?: string } | null;
}

const ORGANISER_FILTER_ALL = 'all';
const ORGANISER_FILTER_EMPTY = '__NO_ORGANISER__';
const ASSIGNMENT_MODE_NEW = 'new';
const ASSIGNMENT_MODE_EXISTING = 'existing';

const AssignGroupCodeModal: React.FC<AssignGroupCodeModalProps> = ({ isOpen, onClose, contracts, refetch }) => {
    const { id } = useParams<{ id: string }>();
    const { handleErrors } = useHandleErrors();
    const [isLoading, setIsLoading] = React.useState(false);
    const [isLoadingExistingGroupCodes, setIsLoadingExistingGroupCodes] = React.useState(false);
    const [selectedContractIds, setSelectedContractIds] = React.useState<string[]>([]);
    const [organiserFilter, setOrganiserFilter] = React.useState<string>(ORGANISER_FILTER_ALL);
    const [assignmentMode, setAssignmentMode] = React.useState<string>(ASSIGNMENT_MODE_NEW);
    const [selectedExistingGroupCodeId, setSelectedExistingGroupCodeId] = React.useState<string>('');
    const [existingGroupCodes, setExistingGroupCodes] = React.useState<ExistingGroupCode[]>([]);
    const [useSameGroupCodeForSameOrganiser, setUseSameGroupCodeForSameOrganiser] = React.useState<boolean>(false);

    React.useEffect(() => {
        if (!isOpen) {
            return;
        }

        setSelectedContractIds(contracts.map((contract) => contract.id));
        setOrganiserFilter(ORGANISER_FILTER_ALL);
        setAssignmentMode(ASSIGNMENT_MODE_NEW);
        setSelectedExistingGroupCodeId('');
        setUseSameGroupCodeForSameOrganiser(false);
    }, [id, isOpen]);

    React.useEffect(() => {
        if (!isOpen || !id) {
            return;
        }

        const fetchExistingGroupCodes = async () => {
            setIsLoadingExistingGroupCodes(true);
            try {
                const response = await (new GroupCodeService()).listByOrder({ orderId: id });
                const responseData = response.getResponseData();

                if (responseData?.success) {
                    setExistingGroupCodes(responseData?.data || []);
                } else {
                    handleErrors(responseData);
                }
            } catch (error) {
                toast.error('Error al cargar los códigos de grupo existentes');
            } finally {
                setIsLoadingExistingGroupCodes(false);
            }
        };

        fetchExistingGroupCodes();
    }, [contracts, isOpen]);

    const organiserOptions = React.useMemo(() => {
        const optionsMap = new Map<string, string>();

        contracts.forEach((contract) => {
            const value = contract.organisingCorporationId || ORGANISER_FILTER_EMPTY;
            const label = contract.organisingCorporationName || 'Sin organizadora';

            if (!optionsMap.has(value)) {
                optionsMap.set(value, label);
            }
        });

        return [
            { label: 'Todas', value: ORGANISER_FILTER_ALL },
            ...Array.from(optionsMap.entries()).map(([value, label]) => ({ label, value })),
        ];
    }, [contracts]);

    const filteredContracts = React.useMemo(() => {
        if (organiserFilter === ORGANISER_FILTER_ALL) {
            return contracts;
        }

        if (organiserFilter === ORGANISER_FILTER_EMPTY) {
            return contracts.filter((contract) => !contract.organisingCorporationId);
        }

        return contracts.filter((contract) => contract.organisingCorporationId === organiserFilter);
    }, [contracts, organiserFilter]);

    const selectedContracts = React.useMemo(
        () => contracts.filter((contract) => selectedContractIds.includes(contract.id)),
        [contracts, selectedContractIds]
    );

    const corporations = React.useMemo(
        () => Array.from(new Set(selectedContracts.map((contract) => contract.organisingCorporationId).filter(Boolean))),
        [selectedContracts]
    );

    const hasSingleCorporation = corporations.length === 1;
    const hasMultipleCorporations = corporations.length > 1;
    const corporationId = hasSingleCorporation ? (corporations[0] as string) : null;
    const canChooseGroupCodeGenerationMode = assignmentMode === ASSIGNMENT_MODE_NEW && selectedContractIds.length > 1;
    const allFilteredSelected = filteredContracts.length > 0 && filteredContracts.every((contract) => selectedContractIds.includes(contract.id));
    const existingGroupCodesForSelectedCorporation = React.useMemo(() => {
        if (!corporationId) {
            return [];
        }

        return existingGroupCodes.filter((groupCode) => groupCode.corporationId === corporationId);
    }, [corporationId, existingGroupCodes]);

    const selectedExistingGroupCode = React.useMemo(() => {
        return existingGroupCodesForSelectedCorporation.find((groupCode) => groupCode.id === selectedExistingGroupCodeId) || null;
    }, [existingGroupCodesForSelectedCorporation, selectedExistingGroupCodeId]);

    const existingGroupCodeOptions = React.useMemo(() => {
        return existingGroupCodesForSelectedCorporation.map((groupCode) => ({
            label: groupCode.code,
            value: groupCode.id,
        }));
    }, [existingGroupCodesForSelectedCorporation]);

    const toggleContractSelection = (contractId: string, checked: boolean) => {
        setSelectedContractIds((prev) => {
            if (checked) {
                if (prev.includes(contractId)) {
                    return prev;
                }
                return [...prev, contractId];
            }

            return prev.filter((id) => id !== contractId);
        });
    };

    const toggleAllFilteredContracts = (checked: boolean) => {
        const filteredIds = filteredContracts.map((contract) => contract.id);

        setSelectedContractIds((prev) => {
            if (checked) {
                return Array.from(new Set([...prev, ...filteredIds]));
            }

            const filteredSet = new Set(filteredIds);
            return prev.filter((id) => !filteredSet.has(id));
        });
    };

    const handleSubmit = async () => {
        if (!id) {
            toast.error('No se ha encontrado el pedido');
            return;
        }

        if (selectedContractIds.length === 0) {
            toast.error('Selecciona al menos un contrato');
            return;
        }

        if (assignmentMode === ASSIGNMENT_MODE_EXISTING) {
            if (!hasSingleCorporation || !corporationId) {
                toast.error('Para usar un código existente, selecciona contratos de una única organizadora');
                return;
            }

            if (!selectedExistingGroupCodeId) {
                toast.error('Selecciona un código de grupo existente');
                return;
            }

            if (!selectedExistingGroupCode) {
                toast.error('El código de grupo seleccionado no es válido');
                return;
            }
        }

        setIsLoading(true);

        try {
            const response = await (new GroupCodeService()).assignContracts({
                contractIds: selectedContractIds,
                orderId: id,
                groupCodeId: assignmentMode === ASSIGNMENT_MODE_EXISTING ? selectedExistingGroupCodeId : undefined,
                useSameGroupCodeForSameOrganiser,
            });

            const responseData = response.getResponseData();
            if (responseData.success) {
                toast.success(responseData.message || 'Código de grupo asignado correctamente');
                onClose();
                refetch();
            } else {
                handleErrors(responseData);
            }
        } catch (error: any) {
            toast.error('Error al asignar código de grupo');
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Modal
            isOpen={isOpen}
            onClose={() => {
                onClose();
            }}
            size='xl'
            overlayClassName='backdrop-blur'
            containerClassName='!shadow-xl'
            className='z-[999] [&_.pointer-events-none]:overflow-visible'
        >
            <div className='m-auto px-7 pt-6 pb-4'>
                <div className='mb-7 flex items-center justify-between'>
                    <Text className="text-xl">Asignar / Cambiar Código de Grupo</Text>
                    <ActionIcon size='sm' variant='text' onClick={() => {
                        onClose();
                    }}>
                        <IoClose className='h-auto w-6' strokeWidth={1.8} />
                    </ActionIcon>
                </div>

                <form
                    onSubmit={(event) => {
                        event.preventDefault();
                        handleSubmit();
                    }}
                    className='space-y-4'
                >
                    <Select
                        label='Filtrar por organizadora'
                        options={organiserOptions}
                        value={organiserOptions.find((option) => option.value === organiserFilter)}
                        onChange={(option: any) => setOrganiserFilter(option?.value || ORGANISER_FILTER_ALL)}
                    />

                    <Select
                        label='Tipo de asignación'
                        options={[
                            { label: 'Crear código nuevo', value: ASSIGNMENT_MODE_NEW },
                            { label: 'Usar código existente', value: ASSIGNMENT_MODE_EXISTING },
                        ]}
                        value={{ label: assignmentMode === ASSIGNMENT_MODE_NEW ? 'Crear código nuevo' : 'Usar código existente', value: assignmentMode }}
                        onChange={(option: any) => {
                            const mode = option?.value || ASSIGNMENT_MODE_NEW;
                            setAssignmentMode(mode);
                            if (mode !== ASSIGNMENT_MODE_EXISTING) {
                                setSelectedExistingGroupCodeId('');
                            }
                        }}
                    />

                    {assignmentMode === ASSIGNMENT_MODE_EXISTING && (
                        <Select
                            label='Código de grupo existente'
                            options={existingGroupCodeOptions}
                            value={existingGroupCodeOptions.find((option) => option.value === selectedExistingGroupCodeId)}
                            onChange={(option: any) => setSelectedExistingGroupCodeId(option?.value || '')}
                            disabled={!hasSingleCorporation || isLoadingExistingGroupCodes || existingGroupCodeOptions.length === 0}
                        />
                    )}

                    {assignmentMode === ASSIGNMENT_MODE_EXISTING && hasSingleCorporation && !isLoadingExistingGroupCodes && existingGroupCodeOptions.length === 0 && (
                        <div className='rounded-md border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-700'>
                            No existen códigos de grupo para la organizadora seleccionada en este pedido.
                        </div>
                    )}

                    {!hasSingleCorporation && selectedContractIds.length > 0 && assignmentMode === ASSIGNMENT_MODE_EXISTING && (
                        <div className='rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700'>
                            Para usar un código existente, los contratos seleccionados deben pertenecer a una única organizadora.
                        </div>
                    )}

                    {canChooseGroupCodeGenerationMode && (
                        <Checkbox
                            checked={useSameGroupCodeForSameOrganiser}
                            onChange={(event) => setUseSameGroupCodeForSameOrganiser(event.target.checked)}
                            label={hasMultipleCorporations
                                ? 'Asignar el mismo código de grupo a los contratos de la misma organizadora'
                                : 'Asignar el mismo código de grupo a todos los contratos seleccionados'}
                        />
                    )}

                    {canChooseGroupCodeGenerationMode && !useSameGroupCodeForSameOrganiser && (
                        <div className='rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700'>
                            Se creará un código de grupo independiente para cada contrato seleccionado.
                        </div>
                    )}

                    {canChooseGroupCodeGenerationMode && useSameGroupCodeForSameOrganiser && hasMultipleCorporations && (
                        <div className='rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700'>
                            Se creará un código compartido por organizadora. Si necesitas 1 grupo = 1 contrato, desmarca esta opción.
                        </div>
                    )}

                    {contracts.length === 0 && (
                        <div className='rounded-md border border-yellow-200 bg-yellow-50 p-3 text-sm text-yellow-700'>
                            No hay contratos disponibles para asignar código de grupo.
                        </div>
                    )}

                    <div className='max-h-72 overflow-auto rounded-md border border-gray-200'>
                        <table className='w-full text-sm'>
                            <thead className='bg-gray-50'>
                                <tr>
                                    <th className='px-3 py-2 text-left'>
                                        <Checkbox
                                            checked={allFilteredSelected}
                                            onChange={(event) => toggleAllFilteredContracts(event.target.checked)}
                                        />
                                    </th>
                                    <th className='px-3 py-2 text-left'>Código Contrato</th>
                                    <th className='px-3 py-2 text-left'>Entidad Organizadora</th>
                                    <th className='px-3 py-2 text-left'>Cliente + NIF</th>
                                    <th className='px-3 py-2 text-left'>Alumno + NIF</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filteredContracts.map((contract) => (
                                    <tr key={contract.id} className='border-t border-gray-100'>
                                        <td className='px-3 py-2'>
                                            <Checkbox
                                                checked={selectedContractIds.includes(contract.id)}
                                                onChange={(event) => toggleContractSelection(contract.id, event.target.checked)}
                                            />
                                        </td>
                                        <td className='px-3 py-2'>{contract.code || 'N/A'}</td>
                                        <td className='px-3 py-2'>{contract.organisingCorporationName || 'N/A'}</td>
                                        <td className='px-3 py-2'>
                                            <div>{contract.clientName || 'N/A'}</div>
                                            <div className='text-gray-500'>{contract.clientNif || 'N/A'}</div>
                                        </td>
                                        <td className='px-3 py-2'>
                                            <div>{contract.studentName || 'N/A'}</div>
                                            <div className='text-gray-500'>{contract.studentNif || 'N/A'}</div>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>

                    <div className='rounded-md border border-blue-200 bg-blue-50 p-3 text-sm text-blue-700'>
                        La fecha de inicio y fin se asignará automáticamente con las fechas del pedido.
                    </div>

                    <div className='flex justify-center pt-4'>
                        <Button
                            type='submit'
                            isLoading={isLoading}
                            disabled={isLoading || selectedContractIds.length === 0 || (assignmentMode === ASSIGNMENT_MODE_EXISTING && !hasSingleCorporation)}
                        >
                            Guardar
                        </Button>
                    </div>
                </form>
            </div>
        </Modal>
    );
};

export default AssignGroupCodeModal;
